import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Mail, Phone, Video, FileText, Activity, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const activityIcons = {
  email: Mail,
  chamada: Phone,
  reuniao: Video,
  nota: FileText,
  estado: TrendingUp,
};

const activityLabels = {
  email: 'Emails',
  chamada: 'Chamadas',
  reuniao: 'Reuniões',
  nota: 'Notas',
  estado: 'Alterações de Estado',
};

export const ActivitiesPage = () => {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || 'all';
  
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(typeParam);

  useEffect(() => {
    fetchData();
  }, []);
  useRefreshListener(() => fetchData());

  const fetchData = async () => {
    try {
      const [activitiesRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/activities?limit=100`),
        axios.get(`${API_URL}/clients`)
      ]);
      setActivities(activitiesRes.data);
      
      const clientsMap = {};
      clientsRes.data.forEach(c => {
        clientsMap[c.id] = c;
      });
      setClients(clientsMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activeTab === 'all'
    ? activities
    : activities.filter(a => a.type === activeTab);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), "dd MMM yyyy 'às' HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="activities-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#2D3E6F]" />
            Atividades
          </h1>
          <p className="text-slate-500 mt-1">
            Histórico de todas as atividades do sistema
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
            <TabsTrigger
              value="all"
              className="rounded-md data-[state=active]:bg-[#2D3E6F] data-[state=active]:text-white"
            >
              Todas
            </TabsTrigger>
            <TabsTrigger
              value="email"
              className="rounded-md data-[state=active]:bg-[#2D3E6F] data-[state=active]:text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              Emails
            </TabsTrigger>
            <TabsTrigger
              value="chamada"
              className="rounded-md data-[state=active]:bg-[#2D3E6F] data-[state=active]:text-white"
            >
              <Phone className="w-4 h-4 mr-2" />
              Chamadas
            </TabsTrigger>
            <TabsTrigger
              value="reuniao"
              className="rounded-md data-[state=active]:bg-[#2D3E6F] data-[state=active]:text-white"
            >
              <Video className="w-4 h-4 mr-2" />
              Reuniões
            </TabsTrigger>
            <TabsTrigger
              value="estado"
              className="rounded-md data-[state=active]:bg-[#2D3E6F] data-[state=active]:text-white"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Estados
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <Card className="rounded-2xl border-slate-100">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner" />
                </div>
              ) : filteredActivities.length === 0 ? (
                <CardContent className="text-center py-16">
                  <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-400">Nenhuma atividade encontrada</p>
                </CardContent>
              ) : (
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {filteredActivities.map((activity, index) => {
                      const Icon = activityIcons[activity.type] || FileText;
                      const client = clients[activity.client_id];
                      
                      return (
                        <div
                          key={activity.id}
                          className="p-4 hover:bg-slate-50/50 transition-colors"
                          data-testid={`activity-item-${index}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-lg ${
                              activity.type === 'estado' 
                                ? activity.new_status === 'concluido' 
                                  ? 'bg-[#B8E02A]/20' 
                                  : activity.new_status === 'cancelado'
                                    ? 'bg-[#E94C88]/20'
                                    : 'bg-[#4DD4E8]/20'
                                : 'bg-slate-100'
                            }`}>
                              <Icon className={`w-5 h-5 ${
                                activity.type === 'estado'
                                  ? activity.new_status === 'concluido'
                                    ? 'text-[#7BA31D]'
                                    : activity.new_status === 'cancelado'
                                      ? 'text-[#E94C88]'
                                      : 'text-[#0e7490]'
                                  : 'text-slate-600'
                              }`} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {activity.new_status && (
                                  <StatusBadge status={activity.new_status} />
                                )}
                                <span className="font-medium text-[#2D3E6F]">
                                  {activity.created_by_name}
                                </span>
                                <span className="text-slate-600">
                                  {activity.description}
                                </span>
                                {client && (
                                  <>
                                    <span className="text-slate-400">•</span>
                                    <Link
                                      to={`/clients/${client.id}`}
                                      className="text-[#4DD4E8] hover:underline font-medium"
                                    >
                                      {client.nome}
                                    </Link>
                                  </>
                                )}
                              </div>
                              
                              {activity.old_status && activity.new_status && (
                                <div className="flex items-center gap-2 mt-1 text-sm">
                                  <StatusBadge status={activity.old_status} />
                                  <span className="text-slate-400">→</span>
                                  <StatusBadge status={activity.new_status} />
                                </div>
                              )}
                              
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDate(activity.created_at)}
                              </p>
                            </div>
                            
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              <AvatarFallback className="bg-[#FFB800]/20 text-[#B45309] text-xs">
                                {getInitials(activity.created_by_name)}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
